import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setStories, setMyStories } from '../slice/storiesSlice';

const STORY_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const getExpiryTs = story => {
    const expiresAt = story.expiresAt || story.expires_at;
    if (expiresAt) {
        const ts = Number(expiresAt);
        return ts > 1_000_000_000_000 ? ts : ts * 1000;
    }
    const createdAt = Number(story.createdAt || story.created_at);
    if (!createdAt) return null;
    const ts = createdAt > 1_000_000_000_000 ? createdAt : createdAt * 1000;
    return ts + STORY_TTL_MS;
};

const filterActive = groups => {
    const now = Date.now();
    return groups
        .map(g => ({
            ...g,
            stories: g.stories.filter(s => {
                const exp = getExpiryTs(s);
                return !exp || exp > now;
            }),
        }))
        .filter(g => g.stories.length > 0);
};

const findNextExpiry = groups => {
    const now = Date.now();
    return groups
        .flatMap(g => g.stories)
        .reduce((min, s) => {
            const exp = getExpiryTs(s);
            return exp && exp > now && exp < min ? exp : min;
        }, Infinity);
};

const hasChanged = (filtered, original) =>
    filtered.length !== original.length || filtered.some((g, i) => g.stories.length !== original[i]?.stories.length);

const useStoryExpiry = () => {
    const dispatch = useDispatch();
    const remoteStories = useSelector(state => state.stories.stories);
    const myStories = useSelector(state => state.stories.myStories);

    // Stable refs so the timeout callback always reads the latest redux state
    // without needing to recreate the callback (which would reset the timer).
    const remoteRef = useRef(remoteStories);
    const myRef = useRef(myStories);
    remoteRef.current = remoteStories;
    myRef.current = myStories;

    const timerRef = useRef(null);

    const runFilter = useCallback(() => {
        const filteredRemote = filterActive(remoteRef.current);
        const filteredMy = filterActive(myRef.current);

        if (hasChanged(filteredRemote, remoteRef.current)) dispatch(setStories(filteredRemote));
        if (hasChanged(filteredMy, myRef.current)) dispatch(setMyStories(filteredMy));

        // Schedule the next run precisely at the moment the earliest story expires.
        // This avoids polling — only one timeout is active at any time.
        clearTimeout(timerRef.current);
        const nextExpiry = Math.min(findNextExpiry(filteredRemote), findNextExpiry(filteredMy));
        if (nextExpiry !== Infinity) {
            const delay = Math.max(nextExpiry - Date.now(), 0);
            timerRef.current = setTimeout(runFilter, delay);
        }
    }, [dispatch]);

    useEffect(() => {
        runFilter(); // filter immediately on mount

        // Re-filter when user returns to the tab after being away / idle.
        // Browsers throttle background timers, so visibilitychange corrects any drift.
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') runFilter();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            clearTimeout(timerRef.current);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [runFilter]);

    // Re-sync the timer whenever the stories list changes externally (e.g. after the
    // initial API fetch populates an empty store). On first mount the store is empty
    // so the effect above sets no timer; this effect catches the transition from [] to
    // a real list and starts the chain.
    useEffect(() => {
        runFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteStories, myStories]);
};

export default useStoryExpiry;
